<template>
  <div>
    <div v-if="!loading">
      <page-header>
        <template #title>
          <h1 class="page-title">
            {{ !!identifier ? 'Update Run' : 'New Run' }}
          </h1>
        </template>
        <template #actions>
          <page-header-action
            tag="form-button"
            :indicator="downloading"
            @clicked="download"
            v-if="run"
          >
            Download Front Sheet
          </page-header-action>
        </template>
      </page-header>

      <div class="run-form">
        <div class="run-form-heading">
          <div class="card mb-3">
            <div class="card-body">
              <selectlist
                :value="form.driver_id"
                @input="setDriver"
                :options="drivers"
                label="Driver"
                name="driver"
                :required="true"
                label-name="full_name"
              ></selectlist>

              <selectlist
                v-if="vehicles.length > 0"
                :value="form.vehicle_id"
                @input="setVehicle"
                :options="vehicles"
                label="Delivery Vehicle"
                name="vehicle"
                label-name="registration"
              ></selectlist>

              <datepicker-input
                :value="form.start_date"
                @input="setStartDate"
                label="Start Date"
                name="start_date"
                :required="true"
              ></datepicker-input>

              <input-block
                v-model="form.depot_instructions"
                name="depot_instructions"
                label="Depot Instructions"
                helper="Add depot instructions here"
              ></input-block>

              <inputbox
                v-model="form.total_mileage"
                name="total_mileage"
                label="Total Mileage"
                helper="Please enter the total mileage for the run"
              ></inputbox>

              <inputbox
                v-model="form.driver_hours"
                name="driver_hours"
                label="Driver Hours"
                helper="Please enter the driver hours for the run"
              ></inputbox>

              <inputbox
                v-model="form.total_hours"
                name="total_hours"
                label="Total Hours"
                helper="Please enter the total hours for the run"
              ></inputbox>

              <div class="form-group">
                <div class="col-md-4">
                  <label class="control-label">
                    Is this an overnight run?
                  </label>
                </div>
                <div class="col-md-8">
                  <toggleswitch
                    :labels="true"
                    v-model="form.has_end_date"
                    name="has_end_date"
                  ></toggleswitch>
                </div>
              </div>

              <datepicker-input
                :value="form.end_date"
                @input="setEndDate"
                label="End Date"
                name="date"
                :required="true"
                v-if="form.has_end_date"
              ></datepicker-input>

              <div
                class="alert alert-danger text-center text-white"
                v-if="existingRunLink"
              >
                There is already a run set up for that driver on that date
                <br />
                <a
                  :href="existingRunLink"
                  class="btn btn-danger"
                  style="margin-top: 7px;"
                >
                  View Run
                </a>
              </div>

              <run-items
                v-model="form.items"
                :jobs="jobs"
                v-if="jobs.length"
              ></run-items>

              <div class="text-right">
                <form-button :indicator="saving" @clicked="submit">
                  Save
                </form-button>
              </div>
            </div>
          </div>
        </div>

        <div class="run-form-body" id="run-form-body">
          <results
            v-model="form.items"
            :jobs="jobs"
            :run="run"
            v-if="jobs.length"
          ></results>
        </div>
      </div>
    </div>

    <div v-else>
      <preloader></preloader>
    </div>
  </div>
</template>

<script>
import Results from '@/components/Modules/Run/Form/Results';
import RunItems from '@/components/Modules/Run/Form/RunItems';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  props: {
    identifier: {
      type: String
    }
  },

  components: {
    PageHeader,
    PageHeaderAction,
    Results,
    RunItems
  },

  data() {
    return {
      form: new window.Form({
        driver_id: null,
        vehicle_id: null,
        start_date: null,
        has_end_date: false,
        end_date: null,
        depot_instructions: null,
        total_mileage: null,
        driver_hours: null,
        total_hours: null,
        items: []
      }),
      jobs: [],
      vehicles: [],
      run: null,
      itemsSetFromRun: false,
      saving: false,
      loading: true,
      downloading: false,
      existingRunLink: undefined
    };
  },

  computed: {
    drivers() {
      return this.$store.getters['ClaimHandlers/drivers'];
    },

    user() {
      return this.$store.getters.user;
    },

    vehicleType() {
      return this.$store.getters['VehicleTypes/all']?.find(
        type => type.name === 'van'
      );
    }
  },

  mounted() {
    this.$store.commit('ClaimHandlers/retrieve');

    this.loadVehicles();

    if (this.identifier) {
      window.Ajax.get(`/runs/${this.identifier}`).then(({ data }) => {
        this.setRun(data);

        // Check if a job has been supplied in the query string and the job
        // isn't already assigned to the route
        if (
          this.$route.query.job &&
          !this.form.items.filter(item => item.job_id == this.$route.query.job)
            .length
        ) {
          this.form.items.push({
            job_id: this.$route.query.job,
            sort_order: 0
          });
        }
      });
    } else {
      if (this.$route.query.job) {
        this.form.items.push({
          job_id: this.$route.query.job,
          sort_order: 0
        });
      }

      if (this.$route.query.date) {
        this.form.date = window.DateManager.parse(this.$route.query.date);

        this.loadJobs(this.form.date);
      }

      this.loading = false;
    }
  },

  methods: {
    submit() {
      this.saving = true;

      const method = this.identifier ? 'put' : 'post';
      const url = this.identifier ? `/runs/${this.identifier}` : '/runs';

      this.form[method](url)
        .then(run => {
          window.toastify.success('Success', 'The run was saved successfully!');

          if (!this.identifier) {
            this.$router.push('/runs');
          } else {
            this.setRun(run);
          }

          this.saving = false;
        })
        .catch(() => {
          window.toastify.error(
            'Error',
            'Sorry, an error occurred while saving the run, please try again'
          );

          this.saving = false;
        });
    },

    loadJobs() {
      const startDate = window.DateManager.format(
        this.form.start_date,
        'YYYY-MM-DD'
      );
      let endDate = window.DateManager.format(this.form.end_date, 'YYYY-MM-DD');

      if (endDate === 'Not available') {
        endDate = null;
      }

      window.Ajax.get(
        `jobs?parts=item,type,notes,jobable&filter[date]=${startDate}${
          endDate ? `,${endDate}` : ''
        }`
      )
        .then(({ data }) => {
          this.jobs = data.filter(
            job => job.type?.name !== 'third_party_movement'
          );
        })
        .catch(() =>
          window.toastify.error(
            'Error',
            'An error occurred while loading the jobs for that date'
          )
        );
    },

    setRun(run) {
      this.run = run;
      this.itemsSetFromRun = false;

      this.form.driver_id = run.driver_id;
      this.form.vehicle_id = run.vehicle_id;
      this.form.start_date = run.start_date
        ? window.DateManager.parse(run.start_date)
        : null;
      this.form.has_end_date = !!run.end_date;
      this.form.end_date = run.end_date
        ? window.DateManager.parse(run.end_date)
        : null;
      this.form.items = run.items;
      this.form.depot_instructions = run.depot_instructions;
      this.form.total_mileage = run.total_mileage;
      this.form.driver_hours = run.driver_hours;
      this.form.total_hours = run.total_hours;

      this.checkForDriversDeliverVehicle();

      this.loadJobs();

      this.loading = false;
    },

    setDriver(driver) {
      this.form.driver_id = driver;

      const vehicle = this.vehicles.find(v => v.driver?.id == driver);

      if (vehicle) {
        this.form.vehicle_id = vehicle.id;
      }

      this.checkForDriversDeliverVehicle();
    },

    setVehicle(vehicle) {
      this.form.vehicle_id = vehicle;
    },

    setStartDate(date) {
      if (
        !this.form.start_date ||
        window.DateManager.differenceInDays(date, this.form.start_date) !== 0
      ) {
        this.form.start_date = date;

        this.loadJobs();
      }
    },

    setEndDate(date) {
      if (
        !this.form.end_date ||
        window.DateManager.differenceInDays(date, this.form.end_date) !== 0
      ) {
        this.form.end_date = date;

        this.loadJobs();
      }
    },

    checkForDriversDeliverVehicle() {
      if (!this.vehicles.length && this.vehicleType) {
        this.loadVehicles();
      }
    },

    loadVehicles() {
      if (this.vehicleType) {
        let identifier = this.user.tenant?.identifier;

        window.Ajax.get(
          `/fleet/${identifier}?filter[type_id]=${this.vehicleType.id}`
        ).then(({ data }) => {
          this.vehicles = data;
        });
      }
    },

    download() {
      if (this.run) {
        this.downloading = true;

        window.Ajax.download(`/runs/${this.identifier}/download`)
          .then(() => {
            this.downloading = false;
          })
          .catch(() => {
            this.downloading = false;

            window.toastify.error(
              'Error',
              'An error occurred while downloading the front sheet'
            );
          });
      }
    },

    // checkForExistingRun() {
    //   if (this.form.driver_id && this.form.start_date) {
    //     window.Ajax.get(`/runs?filter[driver_id]=${this.form.driver_id}&filter[date]=${window.DateManager.format(this.form.date, 'YYYY-MM-DD')}`)
    //         .then(({data}) => {
    //           data = data.filter(run => !this.identifier || run.identifier !== this.identifier);
    //
    //           if (data.length) {
    //             this.existingRunLink = `/runs/${data[0].identifier}/edit`;
    //           }
    //         });
    //   }
    // },

    goToExistingRun() {
      if (this.existingRunLink) {
        this.$router.push(this.existingRunLink);
      }
    }
  }
};
</script>

<style lang="scss">
.run-form {
  .run-form-body {
    height: 100vh;

    .row,
    .col-lg-9 {
      height: 100%;
    }
  }
}

.text-white {
  color: #ffffff;
}
</style>
